/**
 * React hook for managing vendor comparisons with localStorage persistence
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ComparisonItem, 
  UseComparisonReturn,
  ComparisonTableData,
  CompatibilityScore,
  VendorSuggestion,
} from '@/types/comparison';
import { Vendor } from '@/types/vendor';
import {
  saveComparisonState,
  loadComparisonState,
  addComparisonItem,
  removeComparisonItem,
  toggleComparisonItem as toggleComparisonItemStorage,
  isInComparison as checkIsInComparison,
  clearComparisonItems,
  getComparisonCount,
  createComparisonItemFromVendor,
} from '@/lib/comparison-storage';
import {
  generateComparisonTableData,
  calculateVendorCompatibility,
  getVendorSuggestions,
  generateComparisonUrl,
  DEFAULT_COMPARISON_METRICS,
} from '@/lib/comparison';

const MAX_ITEMS = 4;

export function useComparison(allVendors?: Vendor[]): UseComparisonReturn {
  const [items, setItems] = useState<ComparisonItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load comparison state on mount
  useEffect(() => {
    try {
      const loaded = loadComparisonState();
      setItems(loaded.items);
    } catch (err) {
      setError('Failed to load comparison');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save comparison state when items change
  useEffect(() => {
    if (!isLoading) {
      try {
        saveComparisonState({
          items,
          isModalOpen,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        setError('Failed to save comparison');
        console.error(err);
      }
    }
  }, [items, isModalOpen, isLoading]);

  // Add a vendor to comparison
  const addItem = useCallback((vendor: Vendor): boolean => {
    const item = createComparisonItemFromVendor(vendor);
    const result = addComparisonItem(item, items, MAX_ITEMS);
    
    if (result.success) {
      setItems(result.items);
    } else if (result.error) {
      setError(result.error);
    }
    
    return result.success;
  }, [items]);

  // Remove a vendor from comparison
  const removeItem = useCallback((id: string): boolean => {
    const result = removeComparisonItem(id, items);
    
    if (result.success) {
      setItems(result.items);
    }
    
    return result.success;
  }, [items]);

  // Toggle a vendor in comparison (add if not exists, remove if exists)
  const toggleItem = useCallback((vendor: Vendor): boolean => {
    const item = createComparisonItemFromVendor(vendor);
    const result = toggleComparisonItemStorage(item, items, MAX_ITEMS);
    
    if (result.success) {
      setItems(result.items);
    }
    
    return result.added;
  }, [items]);

  // Clear all items
  const clearItems = useCallback(() => {
    try {
      const cleared = clearComparisonItems();
      setItems(cleared);
    } catch (err) {
      setError('Failed to clear comparison');
      throw err;
    }
  }, []);

  // Set modal open state
  const setModalOpen = useCallback((open: boolean) => {
    setIsModalOpen(open);
  }, []);

  // Check if a vendor is in comparison
  const isInComparison = useCallback((id: string): boolean => {
    return checkIsInComparison(id, items);
  }, [items]);

  // Computed properties
  const count = useMemo(() => getComparisonCount(items), [items]);
  const isFull = useMemo(() => count >= MAX_ITEMS, [count]);
  const canAdd = useMemo(() => count < MAX_ITEMS, [count]);

  // Get comparison table data
  const getComparisonTableData = useCallback((): ComparisonTableData => {
    return generateComparisonTableData(items, DEFAULT_COMPARISON_METRICS);
  }, [items]);

  // Calculate compatibility between two vendors
  const calculateCompatibility = useCallback((
    id1: string,
    id2: string
  ): CompatibilityScore | null => {
    const vendor1 = items.find((i: ComparisonItem) => i.id === id1)?.data;
    const vendor2 = items.find((i: ComparisonItem) => i.id === id2)?.data;
    
    if (!vendor1 || !vendor2) return null;
    
    return calculateVendorCompatibility(vendor1, vendor2, DEFAULT_COMPARISON_METRICS);
  }, [items]);

  // Get suggestions for related vendors
  const getSuggestions = useCallback((
    vendor: Vendor,
    limit: number = 3
  ): VendorSuggestion[] => {
    const vendors = allVendors || items.map((i: ComparisonItem) => i.data);
    return getVendorSuggestions(vendor, vendors, limit);
  }, [allVendors, items]);

  // Generate shareable comparison URL
  const getComparisonUrl = useCallback((): string => {
    return generateComparisonUrl(items);
  }, [items]);

  return {
    items,
    count,
    isModalOpen,
    isLoading,
    error,
    addItem,
    removeItem,
    toggleItem,
    clearItems,
    setModalOpen,
    isInComparison,
    isFull,
    canAdd,
    getComparisonTableData,
    calculateCompatibility,
    getSuggestions,
    getComparisonUrl,
  };
}
